import React from 'react';
import styled from 'styled-components';
import { colors } from '@config/colors';
import { media } from '@config/breakpoints';
import { spacing } from '@config/spacing';

/*
 * The mock's page dots: one per section down the right edge, the current
 * one a coral pill. Sized to sit inside the page's side padding on a
 * phone, so they never overlap a card's edge.
 */
const Nav = styled.nav`
  position: fixed;
  top: 50%;
  right: 2px;
  transform: translateY(-50%);
  z-index: 40;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${media.md} {
    right: ${spacing.md};
  }

  @media print {
    display: none;
  }
`;

const Dot = styled.button<{ $current: boolean }>`
  display: block;
  padding: 5px 6px;
  border: 0;
  background: none;
  cursor: pointer;
  line-height: 0;

  &::before {
    content: '';
    display: block;
    width: 7px;
    height: ${({ $current }) => ($current ? '20px' : '7px')};
    border-radius: 999px;
    background: ${({ $current }) => ($current ? colors.accent.coral.extraDark : colors.highlight.extraLight)};
    opacity: ${({ $current }) => ($current ? 1 : 0.6)};
    transition:
      height 0.3s ease,
      background-color 0.3s ease,
      opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid ${colors.highlight.dark};
    outline-offset: 1px;
    border-radius: 999px;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before {
      transition: none;
    }
  }
`;

interface SectionDotsProps {
  /** One name per section, in page order — read out to screen readers. */
  labels: readonly string[];
  current: number;
  onSelect: (index: number) => void;
}

export const SectionDots: React.FC<SectionDotsProps> = ({ labels, current, onSelect }) => (
  <Nav aria-label="Page sections">
    {labels.map((label, i) => (
      <Dot
        key={label}
        type="button"
        $current={i === current}
        aria-label={`Go to ${label}`}
        aria-current={i === current ? 'true' : undefined}
        onClick={() => onSelect(i)}
      />
    ))}
  </Nav>
);
